<template>
    <div>
        <div
            class="dropdown"
            :class="{ open: isOpen, disabled: disabled }"
            @click="toggle"
            v-click-outside
        >
            <div class="dropdown-toggle">
                <div class="dropdown--selected-label">
                    <slot name="selectedLabel">{{ selectedLabel }}</slot>
                </div>
                <div class="dropdown--caret"><span class="caret" /></div>
            </div>
            <ul v-if="isOpen && !options.length" class="dropdown-menu">
                <li class="dropdown-item">Liste vide</li>
            </ul>
            <ul v-if="isOpen && options.length" class="dropdown-menu">
                <div class="dropdown-search" v-if="search" @click.capture.stop="() => {}">
                    <font-awesome-icon icon="search" class="search-icon" />
                    <input
                        type="text"
                        :placeholder="searchPlaceholder"
                        class="search"
                        v-model="searchTerm"
                        @keyup="handleKeyUp"
                    />
                </div>
                <div class="scroller">
                    <component
                        :is="component"
                        v-for="option in filteredOptions"
                        :key="option.id"
                        :value="option"
                        :selected="isSelected(option)"
                        :mark="multiple"
                        @select="handleSelection"
                    />
                </div>
            </ul>
        </div>
        <div v-if="multiple" class="dropdown-selection">
            <div
                v-for="item in limitedValues"
                :key="'s' + item.id"
                class="dropdown-selection--item"
            >
                <font-awesome-icon icon="times" class="icon" @click="handleSelection(item)" />
                <span>{{ item.text }}</span>
            </div>
            <div
                v-if="limited && value.length > limit"
                class="dropdown-selection--item limit-item"
                @click="toggleLimit"
            >
                <font-awesome-icon icon="plus" class="icon" />
                <span>{{ value.length - limit }}</span>
            </div>
            <div
                v-if="!limited && value.length > limit"
                class="dropdown-selection--item limit-item"
                @click="toggleLimit"
            >
                <span class="fix-icon">Voir moins</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        multiple: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Array,
            default: () => [],
        },
        placeholder: String,
        searchPlaceholder: {
            type: String,
            default: 'Chercher...',
        },
        value: [Object, Array],
        limit: {
            type: Number,
            default: 4,
        },
        itemComponent: {
            type: Function,
        },
        search: {
            type: Boolean,
            default: false,
        },
        onKeyUp: {
            type: Function,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isOpen: false,
            limited: true,
            searchTerm: '',
            component: null,
        };
    },
    mounted() {
        this.loader()
            .then(() => {
                this.component = () => this.loader();
            })
            .catch(() => {
                // this.component = () => import('default');
            });
    },
    methods: {
        toggle() {
            if (!this.disabled) {
                this.isOpen = !this.isOpen;
            }
        },
        handleClickOutside() {
            this.isOpen = false;
        },
        toggleLimit() {
            this.limited = !this.limited;
        },
        toggleSelection(item) {
            const value = [...this.value];
            const existIndex = value.findIndex((v) => v.id === item.id);
            if (existIndex > -1) {
                value.splice(existIndex, 1);
                return value;
            }
            return [...value, item];
        },
        handleSelection(event) {
            if (this.disabled) {
                return;
            }

            const value = this.multiple ? this.toggleSelection(event) : event;
            this.$emit('input', value);
            if (this.multiple && !this.limited && value.length <= this.limit) {
                this.limited = true;
            }
            if (!this.multiple) {
                this.isOpen = false;
            }
        },
        isSelected(item) {
            return this.multiple
                ? this.value.some((v) => v.id === item.id)
                : this.value.id === item.id;
        },
        handleKeyUp(event) {
            if (this.onKeyUp) {
                const handled = this.onKeyUp(event, this.searchTerm);
                if (handled) {
                    this.searchTerm = '';
                }
            }
        },
    },
    computed: {
        loader() {
            if (!this.itemComponent) {
                return () => import('./SelectItem');
            }
            return this.itemComponent;
        },
        selectedLabel() {
            if (this.multiple || !this.value.text) {
                return this.placeholder;
            }
            return this.value.text;
        },
        limitedValues() {
            if (this.limited) {
                return this.value.slice(0, this.limit);
            }
            return this.value;
        },
        filteredOptions() {
            if (this.searchTerm) {
                const re = new RegExp(this.searchTerm, 'i');
                return this.options.filter((o) => re.test(o.text));
            }
            return this.options;
        },
    },
};
</script>

<style lang="scss">
.dropdown {
    border: 1px solid #8e919b;
    border-radius: 32px;
    color: #8e919b;
    transition: all 0.25s ease-in-out;
    font-size: 13px;
    position: relative;
    // z-index: 9999;

    &.open .dropdown-toggle {
        color: #3b3e47;
        // z-index: 9999;

        .caret:after {
            border: solid #3b3e47;
            border-width: 0 1px 1px 0;
        }
    }

    &.disabled {
        background-color: #f8f8f8;
    }

    .dropdown-toggle {
        display: flex;
        align-items: center;
        margin: 10px 20px;

        .dropdown--selected-label {
            flex: 1;
            min-width: 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            // > * {
            //     white-space: nowrap;
            //     overflow: hidden;
            //     text-overflow: ellipsis;
            // }
        }
        .dropdown--caret {
            white-space: nowrap;
            margin-left: 5px;

            > span {
                display: inline-block;
                margin-bottom: 3px;

                &:after {
                    content: '';
                    display: block;
                    width: 8px;
                    height: 8px;
                    border: solid #8e919b;
                    border-width: 0 1px 1px 0;
                    -webkit-transform: rotate(45deg);
                    -ms-transform: rotate(45deg);
                    transform: rotate(45deg);
                }
            }
        }
    }

    &.stretch {
        flex: 1;
    }

    .dropdown-menu {
        position: absolute;
        left: 0;
        right: 0;
        border: 1px solid #8e919b;
        border-radius: 7px;
        background-color: #ffffff;
        box-shadow: 0 0 7px 2px rgba(241, 241, 241, 0.5);
        margin-top: 10px;
        // padding: 10px 14px;
        z-index: 9999;

        .scroller {
            //overflow: auto;
            //height: 94px;
            word-break: break-all;
            max-height: 250px;
            overflow-y: auto;
            overflow-x: hidden;
        }
    }

    .dropdown-search {
        // height: 35px;
        border-bottom: 1px solid;
        padding: 10px 20px;
        display: flex;
        align-items: center;

        .search {
            border: none;
            // height: 18px;
            // width: 141px;
            color: #9ca5b0;
            // font-family: "Avenir Next";
            font-size: 13px;
            line-height: 18px;
            //flex: 1;
            margin-left: 8px;
            width: 90%;

            &:focus {
                color: #2a2a2a;
                outline: none;
            }
        }
    }
}
.dropdown-selection {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-top: 9px;

    .dropdown-selection--item {
        display: flex;
        border: 1px solid #8e919b;
        border-radius: 16.5px;
        color: #8e919b;
        margin-right: 5px;
        margin-bottom: 8px;
        padding: 4px 7px;

        span {
            // font-family: "Avenir Next";
            font-size: 14px;
            line-height: 16px;
            margin-right: 9px;
            color: #3b3e47;
        }

        .limit-item {
            border: 1px solid #6c3e91;

            span {
                color: #6c3e91;

                &.fix-icon {
                    margin-left: 9px;
                }
            }
        }
    }
}
</style>
